import { readSystemAssetBindings } from './asset_binding_contract.mjs';
import { readSystemAssetPins } from './asset_pin_contract.mjs';
// UI-free persistence contracts shared by the browser and the backend.
export const PROJECT_DOCUMENT_SCHEMA_VERSION = 2;
export const PROJECT_DOCUMENT_FILE_TYPE = 'ks-configurator-project';
const FORBIDDEN_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

function fail(code, message) {
  throw Object.assign(new TypeError(message), { code });
}

function plainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || Object.getPrototypeOf(prototype) === null;
}

function version(value, label, minimum = 0) {
  if (!Number.isSafeInteger(value) || value < minimum) {
    fail('INVALID_DATA_VERSION', `${label}: ungültige Datenversion.`);
  }
  return value;
}

function synchronous(value, label) {
  if (value && typeof value.then === 'function') {
    Promise.resolve(value).catch(() => undefined);
    fail('ASYNC_DATA_CONTRACT', `${label} muss synchron sein.`);
  }
  return value;
}

/** Strict JSON copy: never silently omit fields, coerce numbers or invoke getters. */
export function cloneSystemData(value, ancestors = new Set()) {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if ((!Array.isArray(value) && !plainObject(value)) || ancestors.has(value) || ancestors.size > 256) {
    fail('INVALID_SYSTEM_DATA', 'Systemdaten müssen endliche, zyklusfreie JSON-Werte sein.');
  }
  ancestors.add(value);
  try {
    const result = Array.isArray(value) ? [] : {};
    const descriptors = Object.getOwnPropertyDescriptors(value);
    if (Array.isArray(value) && Object.keys(value).length !== value.length) {
      fail('INVALID_SYSTEM_DATA', 'Systemdaten dürfen keine lückenhaften Arrays enthalten.');
    }
    for (const key of Reflect.ownKeys(descriptors)) {
      if (Array.isArray(value) && key === 'length') continue;
      const descriptor = descriptors[key];
      if (typeof key !== 'string' || FORBIDDEN_KEYS.has(key) || !descriptor.enumerable || !('value' in descriptor)
        || (Array.isArray(value) && !/^(0|[1-9]\d*)$/.test(key))) {
        fail('INVALID_SYSTEM_DATA', 'Systemdaten enthalten nicht serialisierbare Eigenschaften.');
      }
      result[key] = cloneSystemData(descriptor.value, ancestors);
    }
    return result;
  } finally {
    ancestors.delete(value);
  }
}

function freezeData(value) {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freezeData);
    Object.freeze(value);
  }
  return value;
}

export function normalizeSystemDataContract(manifest, contract) {
  return normalizeDataContract(manifest, contract);
}

function normalizeDataContract(manifest, contract, orderScope = false) {
  const systemId = manifest?.systemId;
  const path = manifest?.data?.path;
  if (typeof systemId !== 'string' || !systemId.trim() || FORBIDDEN_KEYS.has(systemId)
    || !Array.isArray(path) || !path.length || path.length > 8
    || [...path].some(key => typeof key !== 'string' || !key || FORBIDDEN_KEYS.has(key))
    || (path[0] === 'systemOrderData' && !orderScope)
    || path[0] === 'systemDataVersions'
    || !manifest.projectContentPaths?.some(contentPath => Array.isArray(contentPath) && contentPath.length > 0
      && [...contentPath].every((segment, index) => path[index] === segment))) {
    fail('INVALID_DATA_CONTRACT', 'Systemdatenvertrag benötigt einen sicheren Owner und Datenpfad.');
  }
  const currentVersion = version(manifest.data.version, systemId, 1);
  if (!plainObject(contract) || typeof contract.validate !== 'function') {
    fail('INVALID_DATA_CONTRACT', `${systemId}: Datenvalidator fehlt.`);
  }
  const migrations = {};
  if (contract.migrations !== undefined && !plainObject(contract.migrations)) {
    fail('INVALID_DATA_CONTRACT', `${systemId}: ungültige Migrationstabelle.`);
  }
  for (const [key, migration] of Object.entries(contract.migrations || {})) {
    const sourceVersion = Number(key);
    if (String(sourceVersion) !== key || !Number.isSafeInteger(sourceVersion) || sourceVersion < 0
      || sourceVersion >= currentVersion || typeof migration !== 'function') {
      fail('INVALID_DATA_CONTRACT', `${systemId}: ungültiger Migrationsschritt ${key}.`);
    }
    migrations[key] = migration;
  }
  const assetBindingsPath = contract.assetBindingsPath;
  if (assetBindingsPath !== undefined && (!Array.isArray(assetBindingsPath) || !assetBindingsPath.length
    || assetBindingsPath.length > 8 || [...assetBindingsPath].some(key => typeof key !== 'string'
      || !key || key.length > 256 || FORBIDDEN_KEYS.has(key)))) {
    fail('INVALID_DATA_CONTRACT', `${systemId}: ungültiger Assetbindungspfad.`);
  }
  const assetPinsPath = contract.assetPinsPath;
  if (assetPinsPath !== undefined && (!Array.isArray(assetPinsPath) || !assetPinsPath.length
    || assetPinsPath.length > 8 || [...assetPinsPath].some(key => typeof key !== 'string'
      || !key || key.length > 256 || FORBIDDEN_KEYS.has(key)))) {
    fail('INVALID_DATA_CONTRACT', `${systemId}: ungültiger Assetpin-Pfad.`);
  }
  if (contract.orderData !== undefined && !manifest.orderData) {
    fail('INVALID_DATA_CONTRACT', `${systemId}: Auftragsdaten sind nicht deklariert.`);
  }
  const orderData = manifest.orderData ? normalizeDataContract({
    systemId,
    data: { version: manifest.orderData.version, path: ['systemOrderData', systemId, 'data'] },
    projectContentPaths: [['systemOrderData', systemId, 'data']]
  }, contract.orderData, true) : null;
  const normalized = Object.freeze({
    systemId,
    ...(orderData ? { orderData } : {}),
    path: Object.freeze([...path]),
    version: currentVersion,
    defaults: freezeData(cloneSystemData(contract.defaults)),
    ...(assetBindingsPath ? { assetBindingsPath: Object.freeze([...assetBindingsPath]) } : {}),
    ...(assetPinsPath ? { assetPinsPath: Object.freeze([...assetPinsPath]) } : {}),
    validate: contract.validate,
    migrations: Object.freeze(migrations)
  });
  validateSystemData(normalized, normalized.defaults, currentVersion);
  return normalized;
}

export function validateSystemData(contract, raw, fromVersion = 0) {
  version(fromVersion, contract.systemId);
  if (fromVersion > contract.version) {
    fail('UNSUPPORTED_SYSTEM_DATA_VERSION', `${contract.systemId}: Datenversion ${fromVersion} ist neuer als die unterstützte Version ${contract.version}.`);
  }
  const data = freezeData(cloneSystemData(raw));
  if (synchronous(contract.validate(data, Object.freeze({ version: fromVersion })), `${contract.systemId}: Validierung`) !== true) {
    fail('INVALID_SYSTEM_DATA', `${contract.systemId}: ungültige Daten für Version ${fromVersion}.`);
  }
  try { readSystemAssetBindings(contract, data); }
  catch { fail('INVALID_SYSTEM_DATA', `${contract.systemId}: ungültige Assetbindungen.`); }
  try { readSystemAssetPins(contract, data); }
  catch { fail('INVALID_SYSTEM_DATA', `${contract.systemId}: ungültige Assetpins.`); }
  return true;
}

export function migrateSystemData(contract, raw, fromVersion = 0) {
  validateSystemData(contract, raw, fromVersion);
  let data = cloneSystemData(raw);
  for (let sourceVersion = fromVersion; sourceVersion < contract.version; sourceVersion += 1) {
    const migration = contract.migrations[sourceVersion];
    if (typeof migration !== 'function') {
      fail('MISSING_SYSTEM_DATA_MIGRATION', `${contract.systemId}: Migration ${sourceVersion} → ${sourceVersion + 1} fehlt.`);
    }
    data = cloneSystemData(synchronous(migration(data), `${contract.systemId}: Migration`));
    validateSystemData(contract, data, sourceVersion + 1);
  }
  return { data, version: contract.version };
}

export function validateSystemDataVersions(project) {
  if (!plainObject(project)) fail('INVALID_PROJECT_DOCUMENT', 'Projektzustand fehlt oder ist ungültig.');
  if (!Object.hasOwn(project, 'systemDataVersions')) return true;
  const versions = project.systemDataVersions;
  if (!plainObject(versions)) fail('INVALID_DATA_VERSION', 'Ungültige Systemdaten-Versionstabelle.');
  for (const [owner, dataVersion] of Object.entries(versions)) {
    if (!owner.trim() || FORBIDDEN_KEYS.has(owner)) fail('INVALID_DATA_VERSION', 'Ungültiger Owner in Systemdaten-Versionstabelle.');
    version(dataVersion, owner);
  }
  return true;
}

/** Root and inactive drawing sheets; the active mirror is deliberately ignored. */
export function projectDocumentsInState(state) {
  if (!plainObject(state)) fail('INVALID_PROJECT_DOCUMENT', 'Gespeicherter Zustand enthält kein gültiges Projekt.');
  const documents = [];
  const visited = new Set();
  function visit(document, depth) {
    if (visited.has(document) || depth > 8) {
      fail('INVALID_PROJECT_DOCUMENT', 'Ungültige verschachtelte Zeichnungsebenen.');
    }
    visited.add(document);
    const hasEnvelope = Object.hasOwn(document, 'project');
    const project = hasEnvelope ? document.project : document;
    if (!plainObject(project)) fail('INVALID_PROJECT_DOCUMENT', 'Gespeicherter Zustand enthält kein gültiges Projekt.');
    documents.push({ document, project, hasEnvelope });
    const sheets = hasEnvelope ? document.drawingSheets : null;
    if (sheets && Array.isArray(sheets.sheets)) {
      for (const sheet of sheets.sheets) {
        if (sheet?.id === sheets.activeSheetId || !plainObject(sheet?.state)) continue;
        visit(sheet.state, depth + 1);
      }
    }
  }
  visit(state, 0);
  return documents;
}

export function validateProjectDocumentVersions(state) {
  for (const { document, project, hasEnvelope } of projectDocumentsInState(state)) {
    if (hasEnvelope) {
      const schemaVersion = Object.hasOwn(document, 'schemaVersion') ? document.schemaVersion : 0;
      version(schemaVersion, 'Projekt');
      if (schemaVersion > PROJECT_DOCUMENT_SCHEMA_VERSION) {
        fail('UNSUPPORTED_PROJECT_SCHEMA_VERSION', `Projektschema ${schemaVersion} wird noch nicht unterstützt.`);
      }
      if (Object.hasOwn(document, 'fileType') && document.fileType !== PROJECT_DOCUMENT_FILE_TYPE) {
        fail('INVALID_PROJECT_DOCUMENT', 'Unbekannter Projektdateityp.');
      }
      if (document.drawingSheets && Object.hasOwn(document.drawingSheets, 'schemaVersion')
        && document.drawingSheets.schemaVersion !== 1) {
        fail('UNSUPPORTED_PROJECT_SCHEMA_VERSION', 'Die Version der Zeichnungsebenen wird nicht unterstützt.');
      }
    }
    validateSystemDataVersions(project);
  }
  return true;
}
